import type { BusinessVertical } from "@graftus/schema";

/**
 * What the onboarding keeps in the browser before a business doc exists
 * (steps 1 to 3). Once the organisation step creates the business, the server
 * draft takes over and this is cleared.
 */
export const LOCAL_DRAFT_KEY = "graftus_onboarding_v1";
/** The address a sign-in link was sent to, read back when the link returns. */
export const PENDING_EMAIL_KEY = "graftus_onboarding_email";

export interface LocalDraftAccount {
  name: string;
  email: string;
  mobile: string;
}

export interface LocalDraftOrganisation {
  unitName?: string;
  unitNumber?: string;
  headcount?: string;
  /** The chain (DVH) or sales type (Sales) key. */
  choice?: string;
}

export interface LocalDraft {
  v: 1;
  account?: LocalDraftAccount;
  /** ISO instant the sign-in link was last sent. */
  linkSentAt?: string;
  sector?: BusinessVertical;
  organisation?: LocalDraftOrganisation;
}

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function safeStorage(): StorageLike | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

const EMPTY: LocalDraft = { v: 1 };

export function readLocalDraft(storage: StorageLike | null = safeStorage()): LocalDraft {
  try {
    const raw = storage?.getItem(LOCAL_DRAFT_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<LocalDraft> | null;
    if (!parsed || typeof parsed !== "object" || parsed.v !== 1) return EMPTY;
    return { ...EMPTY, ...parsed };
  } catch {
    return EMPTY;
  }
}

/** Merges `patch` into the stored draft and returns the result. */
export function writeLocalDraft(
  patch: Partial<Omit<LocalDraft, "v">>,
  storage: StorageLike | null = safeStorage(),
): LocalDraft {
  const next: LocalDraft = { ...readLocalDraft(storage), ...patch, v: 1 };
  try {
    storage?.setItem(LOCAL_DRAFT_KEY, JSON.stringify(next));
  } catch {
    // Blocked storage: the draft lives in memory for this page view only.
  }
  return next;
}

export function clearLocalDraft(storage: StorageLike | null = safeStorage()): void {
  try {
    storage?.removeItem(LOCAL_DRAFT_KEY);
  } catch {
    // ignore
  }
}

export function readPendingEmail(storage: StorageLike | null = safeStorage()): string | null {
  try {
    const value = storage?.getItem(PENDING_EMAIL_KEY)?.trim();
    return value ? value : null;
  } catch {
    return null;
  }
}

export function writePendingEmail(
  email: string,
  storage: StorageLike | null = safeStorage(),
): void {
  try {
    storage?.setItem(PENDING_EMAIL_KEY, email.trim());
  } catch {
    // ignore
  }
}

export function clearPendingEmail(storage: StorageLike | null = safeStorage()): void {
  try {
    storage?.removeItem(PENDING_EMAIL_KEY);
  } catch {
    // ignore
  }
}
